import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Settings2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import {
  BRANCH_READINESS_LABEL_KEYS,
  type BranchPayrollReadiness,
} from "@/hooks/useBranchPayrollReadiness";

/**
 * Names every payroll setting this branch is missing and links to the exact
 * sub-tab that fixes it.
 *
 * WHY: without this the wizard dead-ends silently. A branch with no payroll
 * policy has no periods, so step 2's period dropdown comes up empty and
 * "Create Payroll Run" stays disabled with nothing on screen saying why.
 *
 * Deliberately not the settings `InfoBanner` — that component is success-styled
 * and private to the settings route.
 */
export const BranchReadinessNotice = ({
  branchName,
  readiness,
}: {
  branchName: string;
  readiness: BranchPayrollReadiness;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const { isLoading, gaps, blockingGaps } = readiness;

  if (isLoading || gaps.length === 0) return null;

  const isBlocked = blockingGaps.length > 0;

  return (
    <div
      className={cn(
        "rounded-xs border px-4 py-3 flex flex-col gap-3",
        isBlocked
          ? "border-danger/30 bg-danger-muted"
          : "border-warning/30 bg-warning-muted",
      )}
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle
          size={16}
          className={cn(
            "mt-0.5 shrink-0",
            isBlocked ? "text-danger" : "text-warning",
          )}
        />
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-foreground">
            {isBlocked
              ? t("company:payrolls.branch_not_ready_to_run_payroll", {
                  branch: branchName,
                })
              : t("company:payrolls.branch_has_unfinished_payroll_settings", {
                  branch: branchName,
                })}
          </p>
          <p className="text-xs text-muted-foreground">
            {isBlocked
              ? t("company:payrolls.branch_readiness_blocked_hint")
              : t("company:payrolls.branch_readiness_warning_hint")}
          </p>
        </div>
      </div>

      <ul className="flex flex-col gap-1.5">
        {gaps.map((gap) => (
          <li key={gap.href}>
            <Link
              to={gap.href}
              className="group flex items-start justify-between gap-3 rounded-xs border border-border bg-surface px-3 py-2 hover:border-primary/40 transition"
            >
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-xs font-semibold text-foreground flex items-center gap-2">
                  {BRANCH_READINESS_LABEL_KEYS[gap.label]
                    ? t(BRANCH_READINESS_LABEL_KEYS[gap.label])
                    : gap.label}
                  {gap.blocking && (
                    <Badge variant="danger">
                      {t("common:labels.required")}
                    </Badge>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t(gap.consequence)}
                </span>
              </div>
              <ArrowRight
                size={14}
                className="mt-0.5 shrink-0 text-primary opacity-60 group-hover:opacity-100 transition"
              />
            </Link>
          </li>
        ))}
      </ul>

      <Button asChild variant="link" size="xs" className="w-fit px-0">
        <Link to={readiness.settingsHref}>
          <Settings2 size={13} />
          {t("company:payrolls.open_all_settings_for_this_branch")}
        </Link>
      </Button>
    </div>
  );
};
